/**
 * BMW fleet whitelist pages — route snippet.
 *
 * These are NOT standalone routes. Every object in `bmwIndividualPartnerRoutes`
 * below belongs inside the `children` array of the existing `/individual-partners`
 * route in `src/router/routes/individual-partners.routes.ts`, alongside the
 * existing `poland-invoice/...` and `fleetcor/...` children. The `path` values are
 * therefore relative — the router resolves them to
 * `/individual-partners/bmw/api-reference` and so on.
 *
 * To wire it up, either paste the five objects directly into that `children`
 * array, or import this array and spread it:
 *
 *   import { bmwIndividualPartnerRoutes } from './bmw-individual-partner.routes';
 *   // ...
 *   children: [
 *     ...existingChildren,
 *     ...bmwIndividualPartnerRoutes,
 *   ],
 *
 * Guarded by `requiredUserGroup: ['bmwfleet']` plus a role, matching
 * `src/constants/menu/sections/bmw.ts` gate for gate (D-48): integration pages need `developer`;
 * pages for business users too (overview, fuel-card orders and process, permission groups,
 * stations) take `requiredAnyRole: ['developer', 'fuel_card_manager']`; ordering a card needs
 * `fuel_card_manager`.
 * The group alone is not enough — `bmwfleet` also holds BMW fleet managers, and
 * these pages are integration tooling.
 *
 * `developer` is assignable to a BMW user only because `roles.ts` lists
 * `FLEET_PARTNER` in its `visibleToPartners`; with the original
 * `[RYD_INTERNAL]`-only list the user-management form stripped it from every
 * non-`ryd` user and this gate could not be passed. See the menu section for why
 * that does not also open the internal developer hub.
 *
 * The router evaluates `requiredRoles.every(...)`, whereas `navmenu.vue` uses
 * `.some(...)`. One role makes those equivalent; a second would not, so keep the
 * list at one entry unless both call sites are revisited.
 *
 * There is deliberately no `requiredPartnerTypes`: the migration path in
 * `services/firebaseAuthService.js` hardcodes `partnerTypes: ['API_PARTNER']`,
 * so gating on `INDIVIDUAL_PARTNER` once rejected the very BMW user these pages
 * are for. Menu and routes must stay in sync: a menu entry the route guard then
 * rejects is worse than no menu entry at all. See INTEGRATION-NOTES.md.
 */

const BmwOverview = () => import('@/pages/individual-partners/bmw/overview/BmwOverview.vue');
const BmwApiReference = () =>
  import('@/pages/individual-partners/bmw/api-reference/BmwApiReference.vue');
const BmwApiGuide = () => import('@/pages/individual-partners/bmw/api-guide/BmwApiGuide.vue');
const BmwWhitelistBatchGuide = () =>
  import('@/pages/individual-partners/bmw/whitelist/batch-guide/BmwWhitelistBatchGuide.vue');
const BmwFuelCardOrders = () =>
  import('@/pages/individual-partners/bmw/fuel-cards/orders/BmwFuelCardOrders.vue');
const BmwFuelCardNewOrder = () =>
  import('@/pages/individual-partners/bmw/fuel-cards/new-order/BmwFuelCardNewOrder.vue');
const BmwPermissionGroups = () =>
  import('@/pages/individual-partners/bmw/fleet-setup/permission-groups/BmwPermissionGroups.vue');
// TEMPORARY: remove with the page once the plant-near sites have switched.
const BmwStationTransitionPlan = () =>
  import('@/pages/individual-partners/bmw/stations/transition-plan/BmwStationTransitionPlan.vue');
const BmwFuelCardStagingTests = () =>
  import('@/pages/individual-partners/bmw/fuel-cards/staging-tests/BmwFuelCardStagingTests.vue');
const BmwFuelCardProcess = () =>
  import('@/pages/individual-partners/bmw/fuel-cards/process/BmwFuelCardProcess.vue');
const BmwWhitelistAudit = () =>
  import('@/pages/individual-partners/bmw/whitelist/change-history/BmwWhitelistAudit.vue');
const BmwSyncMonitor = () =>
  import('@/pages/individual-partners/bmw/whitelist/sync-monitor/BmwSyncMonitor.vue');
const BmwApiInteractionLog = () =>
  import('@/pages/individual-partners/bmw/api-interaction-log/BmwApiInteractionLog.vue');
const BmwVinList = () =>
  import('@/pages/individual-partners/bmw/whitelist/vehicles/BmwVinList.vue');
const BmwDriverList = () =>
  import('@/pages/individual-partners/bmw/whitelist/drivers/BmwDriverList.vue');

// Placeholder pages (shared/bmw-planned-pages.ts): areas not live yet, shown so BMW can review the scope.
const BmwFleetConfiguration = () =>
  import('@/pages/individual-partners/bmw/fleet-setup/configuration/BmwFleetConfiguration.vue');
const BmwDriverOnboarding = () =>
  import('@/pages/individual-partners/bmw/whitelist/driver-onboarding/BmwDriverOnboarding.vue');
const BmwPaymentChannels = () =>
  import('@/pages/individual-partners/bmw/fuelling/payment-channels/BmwPaymentChannels.vue');
const BmwStationCoverage = () =>
  import('@/pages/individual-partners/bmw/stations/coverage/BmwStationCoverage.vue');
const BmwCarWash = () => import('@/pages/individual-partners/bmw/car-wash/overview/BmwCarWash.vue');
// ryd-internal: Circle K POS reporting (DFS PCS) of the BMW wash sales, not a BMW-facing page.
const BmwCircleKPosReporting = () =>
  import('@/pages/individual-partners/bmw/car-wash/circle-k-pos-reporting/BmwCircleKPosReporting.vue');
const BmwCircleKPcsHowItWorks = () =>
  import('@/pages/individual-partners/bmw/car-wash/circle-k-pcs-how-it-works/BmwCircleKPcsHowItWorks.vue');
const BmwTransactions = () =>
  import('@/pages/individual-partners/bmw/invoicing/transactions/BmwTransactions.vue');
const BmwReconciliation = () =>
  import('@/pages/individual-partners/bmw/invoicing/reconciliation/BmwReconciliation.vue');
const BmwInvoices = () =>
  import('@/pages/individual-partners/bmw/invoicing/invoices/BmwInvoices.vue');
const BmwAnomalyReports = () =>
  import('@/pages/individual-partners/bmw/support/anomaly-reports/BmwAnomalyReports.vue');
const BmwSupportProcess = () =>
  import('@/pages/individual-partners/bmw/support/support-process/BmwSupportProcess.vue');

import { AppRouteRecordRaw } from '../types';

/**
 * Access for the partner Circle K PCS pages (read-only PCS API log, How It Works): the dedicated
 * `bmw_circle_k_pcs` role and the bmwfleet or ryd user group. rp-integrations
 * (log) and rp-users (diagrams) enforce both again; ryd users get their own pages under
 * Webhook Management.
 */
const CIRCLE_K_POS_ACCESS = {
  requiredUserGroup: ['bmwfleet', 'ryd'],
  requiredAnyRole: ['bmw_circle_k_pcs'],
};

export const bmwIndividualPartnerRoutes: AppRouteRecordRaw[] = [
  {
    path: 'bmw',
    name: 'bmw-overview',
    component: BmwOverview,
    meta: {
      title: 'BMW Partner Hub | ryd Portal',
      breadcrumb: 'BMW Partner Hub',
      requiredUserGroup: ['bmwfleet'],
      // Developers and business users (D-48)
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
  // Fleet API v1.3.0 is not offered to BMW (2026-10-18): the auth operations BMW needs are part of
  // the BMW API Reference. Old links land there.
  { path: 'bmw/fleet-api-reference', redirect: '/individual-partners/bmw/api-reference' },
  {
    path: 'bmw/api-reference',
    name: 'bmw-api-reference',
    component: BmwApiReference,
    meta: {
      title: 'BMW API Reference | ryd Portal',
      breadcrumb: 'BMW API Reference',
      requiredUserGroup: ['bmwfleet'],
      requiredRoles: ['developer'],
    },
  },
  {
    path: 'bmw/api-guide',
    name: 'bmw-api-guide',
    component: BmwApiGuide,
    meta: {
      title: 'BMW API Guide | ryd Portal',
      breadcrumb: 'BMW API Guide',
      requiredUserGroup: ['bmwfleet'],
      requiredRoles: ['developer'],
    },
  },
  {
    path: 'bmw/fuel-cards',
    name: 'bmw-fuel-card-orders',
    component: BmwFuelCardOrders,
    meta: {
      title: 'BMW Fuel Card Orders | ryd Portal',
      breadcrumb: 'BMW Fuel Card Orders',
      requiredUserGroup: ['bmwfleet'],
      // Developers and business users (D-48)
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
  {
    path: 'bmw/fuel-cards/new',
    name: 'bmw-fuel-card-new-order',
    component: BmwFuelCardNewOrder,
    meta: {
      title: 'Order a BMW Fuel Card | ryd Portal',
      breadcrumb: 'Order a Fuel Card',
      requiredUserGroup: ['bmwfleet'],
      // Ordering needs the manager role, in the portal and in rp-integrations (D-48)
      requiredRoles: ['fuel_card_manager'],
    },
  },
  {
    path: 'bmw/fuel-card-process',
    name: 'bmw-fuel-card-process',
    component: BmwFuelCardProcess,
    meta: {
      title: 'BMW Fuel Card Process | ryd Portal',
      breadcrumb: 'BMW Fuel Card Process',
      requiredUserGroup: ['bmwfleet'],
      // Developers and business users (D-48)
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
  {
    // The page itself asks to switch when production is selected
    path: 'bmw/fuel-cards/staging-tests',
    name: 'bmw-fuel-card-staging-tests',
    component: BmwFuelCardStagingTests,
    meta: {
      title: 'BMW Fuel Card Staging Tests | ryd Portal',
      breadcrumb: 'BMW Fuel Card Staging Tests',
      requiredUserGroup: ['bmwfleet'],
      // Developers and business users (D-48); ordering a case needs fuel_card_manager
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
  // Superseded 2026-10-18 by the merged BMW API Reference; kept so old links still land.
  { path: 'bmw/whitelist-api-reference', redirect: '/individual-partners/bmw/api-reference' },
  // Hidden 2026-10-18 (INTEGRATION-NOTES §31): BMW sends whitelists through the API, so the approach guides and the
  // simulator no longer apply. Files kept in whitelist/_hidden/; old links land on the batch guide.
  {
    path: 'bmw/whitelist-batch-guide',
    name: 'bmw-whitelist-batch-guide',
    component: BmwWhitelistBatchGuide,
    meta: {
      title: 'BMW Whitelist Batch API Guide | ryd Portal',
      breadcrumb: 'BMW Whitelist Batch API Guide',
      requiredUserGroup: ['bmwfleet'],
      requiredRoles: ['developer'],
    },
  },
  { path: 'bmw/whitelist-concept', redirect: '/individual-partners/bmw/whitelist-batch-guide' },
  {
    path: 'bmw/whitelist-file-contract',
    redirect: '/individual-partners/bmw/whitelist-batch-guide',
  },
  {
    path: 'bmw/whitelist-best-practices',
    redirect: '/individual-partners/bmw/whitelist-batch-guide',
  },
  {
    path: 'bmw/change-history',
    name: 'bmw-change-history',
    component: BmwWhitelistAudit,
    meta: {
      title: 'BMW Change History | ryd Portal',
      breadcrumb: 'BMW Change History',
      requiredUserGroup: ['bmwfleet'],
      requiredRoles: ['developer'],
    },
  },
  {
    path: 'bmw/sync-monitor',
    name: 'bmw-sync-monitor',
    component: BmwSyncMonitor,
    meta: {
      title: 'BMW Sync Monitor | ryd Portal',
      breadcrumb: 'BMW Sync Monitor',
      requiredUserGroup: ['bmwfleet'],
      requiredRoles: ['developer'],
    },
  },
  {
    path: 'bmw/api-interaction-log',
    name: 'bmw-api-interaction-log',
    component: BmwApiInteractionLog,
    meta: {
      title: 'BMW API Interaction Log | ryd Portal',
      breadcrumb: 'BMW API Interaction Log',
      requiredUserGroup: ['bmwfleet'],
      requiredRoles: ['developer'],
    },
  },
  {
    path: 'bmw/vins',
    name: 'bmw-vin-list',
    component: BmwVinList,
    meta: {
      title: 'BMW Uploaded VINs | ryd Portal',
      breadcrumb: 'BMW Uploaded VINs',
      requiredUserGroup: ['bmwfleet'],
      requiredRoles: ['developer'],
    },
  },
  {
    path: 'bmw/drivers',
    name: 'bmw-driver-list',
    component: BmwDriverList,
    meta: {
      title: 'BMW Uploaded Drivers | ryd Portal',
      breadcrumb: 'BMW Uploaded Drivers',
      requiredUserGroup: ['bmwfleet'],
      requiredRoles: ['developer'],
    },
  },
  // The stock-pool card pages were replaced by the order flow (TIM orders through the API).
  { path: 'bmw/fleet-cards', redirect: '/individual-partners/bmw/fuel-card-process' },
  { path: 'bmw/fleet-card-api-reference', redirect: '/individual-partners/bmw/api-reference' },
  {
    path: 'bmw/permission-groups',
    name: 'bmw-permission-groups',
    component: BmwPermissionGroups,
    meta: {
      title: 'BMW Permission Groups | ryd Portal',
      breadcrumb: 'BMW Permission Groups',
      requiredUserGroup: ['bmwfleet'],
      // Developers and business users (D-48)
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
  {
    path: 'bmw/station-transition-plan',
    name: 'bmw-station-transition-plan',
    component: BmwStationTransitionPlan,
    meta: {
      title: 'BMW Station Transition Plan | ryd Portal',
      breadcrumb: 'BMW Station Transition Plan',
      requiredUserGroup: ['bmwfleet'],
      // Developers and business users (D-48)
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
  { path: 'bmw/import-simulator', redirect: '/individual-partners/bmw/whitelist-batch-guide' },
  {
    path: 'bmw/fleet-configuration',
    name: 'bmw-fleet-configuration',
    component: BmwFleetConfiguration,
    meta: {
      title: 'BMW Fleet Configuration | ryd Portal',
      breadcrumb: 'BMW Fleet Configuration',
      requiredUserGroup: ['bmwfleet'],
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
  {
    path: 'bmw/driver-onboarding',
    name: 'bmw-driver-onboarding',
    component: BmwDriverOnboarding,
    meta: {
      title: 'BMW Driver Onboarding | ryd Portal',
      breadcrumb: 'BMW Driver Onboarding',
      requiredUserGroup: ['bmwfleet'],
      requiredRoles: ['developer'],
    },
  },
  {
    path: 'bmw/payment-channels',
    name: 'bmw-payment-channels',
    component: BmwPaymentChannels,
    meta: {
      title: 'BMW Payment Channels | ryd Portal',
      breadcrumb: 'BMW Payment Channels',
      requiredUserGroup: ['bmwfleet'],
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
  {
    path: 'bmw/station-coverage',
    name: 'bmw-station-coverage',
    component: BmwStationCoverage,
    meta: {
      title: 'BMW Station Coverage | ryd Portal',
      breadcrumb: 'BMW Station Coverage',
      requiredUserGroup: ['bmwfleet'],
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
  {
    path: 'bmw/car-wash/circle-k-pos-reporting',
    name: 'bmw-circle-k-pos-reporting',
    component: BmwCircleKPosReporting,
    meta: {
      title: 'Circle K POS Reporting | ryd Portal',
      breadcrumb: 'Circle K POS Reporting',
      ...CIRCLE_K_POS_ACCESS,
    },
  },
  {
    path: 'bmw/car-wash/circle-k-pcs-how-it-works',
    name: 'bmw-circle-k-pcs-how-it-works',
    component: BmwCircleKPcsHowItWorks,
    meta: {
      title: 'How Circle K POS Reporting Works | ryd Portal',
      breadcrumb: 'How Circle K POS Reporting Works',
      ...CIRCLE_K_POS_ACCESS,
    },
  },

  {
    path: 'bmw/car-wash',
    name: 'bmw-car-wash',
    component: BmwCarWash,
    meta: {
      title: 'BMW Car Wash | ryd Portal',
      breadcrumb: 'BMW Car Wash',
      requiredUserGroup: ['bmwfleet'],
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
  {
    path: 'bmw/transactions',
    name: 'bmw-transactions',
    component: BmwTransactions,
    meta: {
      title: 'BMW Transactions | ryd Portal',
      breadcrumb: 'BMW Transactions',
      requiredUserGroup: ['bmwfleet'],
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
  {
    path: 'bmw/reconciliation',
    name: 'bmw-reconciliation',
    component: BmwReconciliation,
    meta: {
      title: 'BMW Reconciliation | ryd Portal',
      breadcrumb: 'BMW Reconciliation',
      requiredUserGroup: ['bmwfleet'],
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
  {
    path: 'bmw/invoices',
    name: 'bmw-invoices',
    component: BmwInvoices,
    meta: {
      title: 'BMW Invoices | ryd Portal',
      breadcrumb: 'BMW Invoices',
      requiredUserGroup: ['bmwfleet'],
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
  {
    path: 'bmw/anomaly-reports',
    name: 'bmw-anomaly-reports',
    component: BmwAnomalyReports,
    meta: {
      title: 'BMW Anomaly Reports | ryd Portal',
      breadcrumb: 'BMW Anomaly Reports',
      requiredUserGroup: ['bmwfleet'],
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
  {
    path: 'bmw/support',
    name: 'bmw-support',
    component: BmwSupportProcess,
    meta: {
      title: 'BMW Support Process | ryd Portal',
      breadcrumb: 'BMW Support Process',
      requiredUserGroup: ['bmwfleet'],
      requiredAnyRole: ['developer', 'fuel_card_manager'],
    },
  },
];
